import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import i18next from "i18next";
import axios from "axios";
import PropTypes from "prop-types";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import CompanyInvoice from "./components/CompanyInvoice/CompanyInvoice";
import Documents from "./components/Documents";
import Header from "./components/Header/Header";
import PersonInChargeList from "../../PersonInCharge/PersonInChargeList";
import ContextualComment from "./components/ContextualComment/ContextualComment";
import SiteValidationBanner from "./components/SiteValidationBanner";
import SiteInfo from "./components/SiteInfo/SiteInfo";
import QuoteView from "./components/QuoteView/QuoteView";

import "./KickOffView.scss";
import {
  SiteContainer,
  SectionContainer,
  MainWrapper,
  SiteInfoContainer,
  SiteSection,
  SiteWrapper,
  CompanyContainer,
} from "./KickOffView.styled";

const KickOffView = (props) => {
  const { t, objectId, setIsRefeshSiteData, defaultQuoteId } = props;
  const [data, setData] = useState("");
  const [siteKoStatus, setSiteKoStatus] = useState("");
  const [companyInvoiceError, setCompanyInvoiceError] = useState([]);
  const [siteInfoError, setSiteInfoError] = useState([]);
  const [documentError, setDocumentError] = useState([]);
  const [quoteKoStatus, setQuoteKOStatus] = useState("");
  const [isQuote, setQuoteClickStatus] = useState(false);
  const [isRefreshData, setIsRefreshData] = useState(false);
  const [tabId, setTabID] = useState("");
  const [currentQuoteTab, showQuoteTab] = useState();
  const [showSiteInfo, setSiteInfoVisiblity] = useState(true);
  const [contextualComment, setContextualComment] = useState("");
  const [isSiteValidateLoaderVisible, setSiteValidateLoader] = useState(false);
  const generalParams = useGetGeneralParameters();
  const countries = generalParams?.countryList;
  // Set Country
  useEffect(() => {
    if (data.countryId !== undefined || data.countryId !== null) {
      const isCountryPresent = countries.find(
        ({ id }) => id === data.countryId
      );
      if (isCountryPresent) {
        setCountry(isCountryPresent.label);
      }
    }
  }, [data]);

  const fetchKickOffInfo = () => {
    let isMount = true;

    axios
      .get(`/sites/${objectId}/kickoff`)
      .then((result) => {
        if (result.status === 200 && isMount) {
          setData(result.data[0]);
          setSiteKoStatus(result.data[0]?.koStatus);
          setContextualComment(
            result.data[0].koComment ? result.data[0].koComment : ""
          );
        }
      })
      .catch((error) => {});

    return () => {
      isMount = false;
    };
  };

  useEffect(() => {
    fetchKickOffInfo();
  }, []);

  useEffect(() => {
    if (isRefreshData) {
      fetchKickOffInfo();
      setIsRefreshData(false);
    }
  }, [isRefreshData]);

  // Handle defaultQuoteId from URL - auto-select the quote tab
  useEffect(() => {
    if (defaultQuoteId && data) {
      const quoteId = parseInt(defaultQuoteId, 10);
      if (!isNaN(quoteId)) {
        setQuoteClickStatus(true);
        setTabID(quoteId);
        setSiteInfoVisiblity(false);
        showQuoteTab(quoteId);
      }
    }
  }, [defaultQuoteId, data]);

  // Contextual comment is common to both site info tab and for each quote tab.
  const updateContextComment = (comment) => {
    setContextualComment(comment);
  };

  const handleTabItemClick = (id) => {
    if (id) {
      setQuoteClickStatus(true);
      setTabID(id);
      setSiteInfoVisiblity(false);
    } else {
      setQuoteClickStatus(false);
      setSiteInfoVisiblity(true);
      setTabID("");
    }
  };

  useEffect(() => {
    if (documentError?.length > 0) {
      const docError = [];
      let count = 0;
      if (data?.documents?.length === 0 || !data?.documents) {
        docError.push("KICK_OFF_PAGE_DOCUMENTS_ALERT_MISSING_SITE_PROTOCOL");
      }
      if (data?.documents?.length > 0) {
        data?.documents.map((document) => {
          if (parseInt(document?.type?.id, 10) === 23) {
            count += 1;
          }
        });
      }
      if (count === 0 && data?.documents?.length > 0) {
        docError.push("KICK_OFF_PAGE_DOCUMENTS_ALERT_MISSING_SITE_PROTOCOL");
      }

      if (count > 1 && data?.documents?.length > 0) {
        docError.push("KICK_OFF_PAGE_DOCUMENTS_ALERT_MULTIPLE_SITE_PROTOCOL");
      }
      setDocumentError(docError);
    }
  }, [data]);

  const validateBanner = () => {
    const companyError = [];
    const docError = [];
    const siteError = [];
    let count = 0;
    let errorInvoice = 0;
    const companyPostalCode =
      data?.company?.billingAddress?.postalCode ??
      data?.company?.billingAddress?.zipcode;
    if (
      !companyPostalCode ||
      !data?.company?.billingAddress?.city ||
      !data?.company?.billingAddress?.street
    ) {
      companyError.push(
        "KICK_OFF_PAGE_SITE_INFORMATION_INVOICING_ADDRESS_MISSING_ERROR"
      );
    }
    if (data.company?.invoicingContacts?.length === 0) {
      companyError.push(
        "KICK_OFF_PAGE_SITE_INFORMATION_INVOICING_CONTACTS_MISSING_ERROR"
      );
    }
    if (data?.documents?.length === 0 || !data?.documents) {
      docError.push("KICK_OFF_PAGE_DOCUMENTS_ALERT_MISSING_SITE_PROTOCOL");
    }
    if (data?.documents?.length > 0) {
      data?.documents.map((document) => {
        if (parseInt(document?.type?.id, 10) === 23) {
          count += 1;
        }
      });
    }
    if (count === 0 && data?.documents?.length > 0) {
      docError.push("KICK_OFF_PAGE_DOCUMENTS_ALERT_MISSING_SITE_PROTOCOL");
    }

    if (count > 1 && data?.documents?.length > 0) {
      docError.push("KICK_OFF_PAGE_DOCUMENTS_ALERT_MULTIPLE_SITE_PROTOCOL");
    }

    const sitePostalCode = data?.address?.postalCode ?? data?.address?.zipcode;
    if (!sitePostalCode || !data?.address?.city || !data?.address?.street) {
      siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_ADDRESS_MISSING_ERROR");
    }
    if (!data.estimatedStartDate || !data.estimatedEndDate) {
      siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_DATE_MISSING_ERROR");
    }
    if (data?.workingHours?.length === 0 || !data?.workingHours) {
      siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_HOURS_MISSING_ERROR");
    }
    if (![0, 1, 2].includes(data?.budgetType)) {
      siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
      errorInvoice = 1;
    }
    if (data?.budgetType === 1 && errorInvoice === 0) {
      if (!data?.siteBudgetAmount && data?.siteBudgetAmount !== 0) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
        errorInvoice = 1;
      }
    }
    if (data?.budgetType === 2 && errorInvoice === 0) {
      const delegatedCompanyName = data?.delegatedCompany?.id
        ? data?.delegatedCompany?.name
        : data?.delegatedCompany?.nameText;
      if (
        !data?.paymentDelegationAmount &&
        data?.paymentDelegationAmount !== 0
      ) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
        errorInvoice = 1;
      }
      if (!delegatedCompanyName) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
        errorInvoice = 1;
      }
    }

    setSiteInfoError(siteError);
    setCompanyInvoiceError(companyError);
    setDocumentError(docError);

    if (
      siteError.length === 0 &&
      companyError.length === 0 &&
      docError.length === 0
    ) {
      setSiteValidateLoader(true);
      axios
        .put(`/sites/${objectId}/updateKoStatus`)
        .then((result) => {
          setSiteValidateLoader(false);
          setData(result.data[0]);
          setSiteKoStatus(result.data[0]?.koStatus);
          setContextualComment(
            result.data[0].koComment ? result.data[0].koComment : ""
          );
        })
        .catch((error) => {
          if (window.debugMode) {
            console.error(
              "Error: an error occurred while fetching places",
              error
            );
          }
          setSiteValidateLoader(false);
        });
    }
  };

  return (
    <SiteContainer>
      <Header
        objectId={objectId}
        siteId={data?.id}
        siteKoStatus={siteKoStatus}
        siteName={data?.name}
        companyName={data?.company?.name}
        koStatus={isQuote ? quoteKoStatus : siteKoStatus}
        isQuote={isQuote}
        validatedQuotes={data?.quotes}
        showSiteInfo={showSiteInfo}
        setSiteInfoVisiblity={setSiteInfoVisiblity}
        onTabClick={handleTabItemClick}
        currentQuoteTab={currentQuoteTab}
        country={data?.platformCountry}
      />
      <SectionContainer>
        {showSiteInfo && (
          <MainWrapper>
            <SiteInfoContainer>
              <PersonInChargeList
                objectId={parseInt(data?.id, 10)}
                objectType="site"
                columnName="siteId"
                data={data?.personInCharge}
                editOption={false}
              />
              <ContextualComment
                comment={contextualComment}
                isAuthorizedSalesman={parseInt(data?.isAuthorizedSalesman)}
                koStatus={parseInt(siteKoStatus)}
                jobsite_id={data?.id}
                updateContextComment={updateContextComment}
              />
              <SiteSection>
                <SiteWrapper>
                  <SiteInfo
                    setData={setData}
                    data={data}
                    isAuthorizedSalesman={parseInt(data?.isAuthorizedSalesman)}
                    koStatus={parseInt(siteKoStatus)}
                    siteInfoError={siteInfoError}
                    setSiteInfoError={setSiteInfoError}
                  />
                </SiteWrapper>
              </SiteSection>
              <CompanyContainer>
                <CompanyInvoice
                  companyData={data?.company}
                  companyInvoiceError={companyInvoiceError}
                />
                <Documents
                  documentList={data.documents}
                  id={data.id}
                  koStatus={parseInt(data?.koStatus, 10)}
                  isAuthorizedSalesman={parseInt(
                    data?.isAuthorizedSalesman,
                    10
                  )}
                  isAuthorizedStudy={parseInt(data?.isAuthorizedStudy, 10)}
                  entity="siteId"
                  setIsRefreshData={setIsRefreshData}
                  documentError={documentError}
                />
              </CompanyContainer>
              {parseInt(data?.koStatus, 10) === 1 &&
                parseInt(data?.isAuthorizedSalesman, 10) !== 0 && (
                  <SiteValidationBanner
                    validateBanner={validateBanner}
                    isSiteValidateLoaderVisible={isSiteValidateLoaderVisible}
                  />
                )}
            </SiteInfoContainer>
          </MainWrapper>
        )}
        {isQuote && tabId && (
          <QuoteView
            quoteId={tabId}
            setIsRefeshSiteData={setIsRefeshSiteData}
          />
        )}
      </SectionContainer>
    </SiteContainer>
  );
};

KickOffView.propTypes = {
  t: PropTypes.func.isRequired,
  objectId: PropTypes.number,
  defaultQuoteId: PropTypes.string,
};

KickOffView.defaultProps = {
  objectId: null,
  defaultQuoteId: null,
};

export default withTranslation()(KickOffView);
